import { captureRemixErrorBoundaryError, withSentry } from "@sentry/remix";
import { LinksFunction, LoaderFunction, json } from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  useLoaderData,
  useRouteError,
} from "@remix-run/react";
import stylesheet from "./tailwind.css?url";
import agGridCoreStylesheet from "ag-grid-enterprise/styles/ag-grid.css?url";
import agGridQuartzStylesheet from "ag-grid-enterprise/styles/ag-theme-quartz.css?url";
import toastStyles from "react-toastify/ReactToastify.css?url";
import { Theme, ThemeProvider, useTheme } from "./utils/theme";
import { SessionManager } from "./.server/services/session";
import clsx from "clsx";
import { getToast } from "remix-toast";
import { ToastContainer, toast as notify } from "react-toastify";
import { useEffect } from "react";
import InformationCircleIcon from "@heroicons/react/24/solid/esm/InformationCircleIcon";
import { authenticator } from "./.server/services/authentication";
import { SessionUser } from "./foundation/types/sessionUser";

export type LoaderData = {
  theme: Theme;
  env: {
    AG_GRID_ENTERPRISE_KEY: string;
    VERCEL_ENVIRONMENT: string;
  };
  toast: any;
  headers: any;
  user: SessionUser | null;
};

export const loader: LoaderFunction = async ({ request }) => {
  const themeSession = await SessionManager.fromRequest(request);
  const user = await authenticator.isAuthenticated(request);
  const { toast, headers } = await getToast(request);
  const data: LoaderData = {
    theme: themeSession.getTheme(),
    env: {
      AG_GRID_ENTERPRISE_KEY: process.env.AG_GRID_ENTERPRISE_KEY,
      VERCEL_ENVIRONMENT: process.env.VERCEL_ENVIRONMENT ?? "",
    },
    toast,
    headers,
    user,
  };

  return json(data);
};

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: stylesheet as any },
  { rel: "stylesheet", href: "https://rsms.me/inter/inter.css" },
  { rel: "stylesheet", href: agGridCoreStylesheet },
  { rel: "stylesheet", href: agGridQuartzStylesheet },
  { rel: "stylesheet", href: toastStyles },
];

function App() {
  const [theme] = useTheme();
  const data = useLoaderData<LoaderData>();

  useEffect(() => {
    if (data.toast) {
      notify(data.toast.message, {
        type: data.toast.type,
      });
    }
  }, [data.toast]);

  return (
    <html suppressHydrationWarning className={`h-full ${clsx(data.theme)}`}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        <Meta />
        <Links />
      </head>
      <body suppressHydrationWarning className="h-full dark:bg-gray-950">
        <Scripts />
        <script
          dangerouslySetInnerHTML={{
            __html: `window.env = ${JSON.stringify(data.env)}`,
          }}
        />
        {data.env.VERCEL_ENVIRONMENT === "preview" ? (
          <div className="fixed text-center top-0 left-0 z-50 w-full px-2 py-1 text-sm text-white bg-blue-600 border border-black rounded-br">
            <InformationCircleIcon className="inline w-5 h-5 mr-1 -mt-1" />
            NOTICE: This is the PREVIEW environment
          </div>
        ) : null}
        <Outlet />
        <ToastContainer />
      </body>
    </html>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  try {
    captureRemixErrorBoundaryError(error);
  } catch (err) {
    console.error(err);
  }
  return (
    <html>
      <head>
        <title>Oh no!</title>
        <Meta />
        <Links />
      </head>
      <body>
        {/* add the UI you want your users to see */}
        <Scripts />
      </body>
    </html>
  );
}

function AppWithProviders() {
  const data = useLoaderData<LoaderData>();
  return (
    <ThemeProvider specifiedTheme={data.theme}>
      <App />
    </ThemeProvider>
  );
}

export default withSentry(AppWithProviders);
